/**
 * The client's copy of the map.
 *
 * Imported rather than fetched: prediction needs the collision world before the
 * first input, and a fetch would either block the join or leave the first frames
 * unpredicted. The bundler inlines the same JSON file the server reads from disk,
 * so both sides start from identical bytes — and `mapContentHash` is what proves it
 * stayed that way after a deploy (see `shared/src/welcome-codec.ts`).
 *
 * When maps become selectable per room this becomes a small registry keyed by name,
 * with `import.meta.glob`. One map does not need that yet.
 */
// The `with { type: 'json' }` attribute is not optional decoration: the acceptance
// harnesses in realtime/src/scripts import this module and type-check under
// `module: NodeNext`, where a bare JSON import is an error. Vite and esbuild both
// understand the attribute, so one form satisfies every toolchain that reads this
// file.
import greyboxJson from '../../../../shared/maps/greybox-01.json' with { type: 'json' };
import blacklineKasbahJson from '../../../../shared/maps/blackline-kasbah.json' with { type: 'json' };
import blacklineFoundryJson from '../../../../shared/maps/blackline-foundry.json' with { type: 'json' };
import blacklineHarbourJson from '../../../../shared/maps/blackline-harbour.json' with { type: 'json' };
import blacklineDuneIiJson from '../../../../shared/maps/blackline-dune-ii.json' with { type: 'json' };
import blacklineRelayJson from '../../../../shared/maps/blackline-relay.json' with { type: 'json' };
import blacklineMonteforteJson from '../../../../shared/maps/blackline-monteforte.json' with { type: 'json' };
import blacklineOvergrowthJson from '../../../../shared/maps/blackline-overgrowth.json' with { type: 'json' };
import blacklineReactorJson from '../../../../shared/maps/blackline-reactor.json' with { type: 'json' };
import {
  DEFAULT_MAP_NAME,
  formatMapHash,
  type MapDefinition,
  mapContentHash,
  parseMapDefinition,
  toCollisionMap,
} from '../../../../shared/src/map/definition.js';
import type { CollisionMap } from '../../../../shared/src/collision/brushes.js';

export interface ClientMap {
  definition: MapDefinition;
  collision: CollisionMap;
  hash: number;
}

function load(raw: unknown, source: string): ClientMap {
  const definition = parseMapDefinition(raw, source);
  return {
    definition,
    collision: toCollisionMap(definition),
    hash: mapContentHash(definition),
  };
}

/**
 * Parsed at module load. If a bundled map is malformed the page fails immediately
 * and visibly, which is the right time to find out — the alternative is a client
 * that predicts against no floor.
 */
export const GREYBOX_01: ClientMap = load(greyboxJson, 'greybox-01.json');
// The 2026-09-08 rebuild: two maps built for the PBR renderer and placed lights, replacing the
// seven greybox-with-props maps (`design/gdd/maps.md` §3b′). Generators in `tools/maps/`.
export const BLACKLINE_KASBAH: ClientMap = load(blacklineKasbahJson, 'blackline-kasbah.json');
export const BLACKLINE_FOUNDRY: ClientMap = load(blacklineFoundryJson, 'blackline-foundry.json');
// Third rotation map (2026-09-08, the rotation needed more maps): a container dock at dusk, `gen-harbour.ts`.
export const BLACKLINE_HARBOUR: ClientMap = load(blacklineHarbourJson, 'blackline-harbour.json');
// Two maps from the outside designer's Blender pack (2026-09-09): collision JSON in our schema
// plus a separate visual GLB in `visual.gltf`, the first maps to use that slot. Dune II is an
// open sand town; Relay is a small station block-out.
export const BLACKLINE_DUNE_II: ClientMap = load(blacklineDuneIiJson, 'blackline-dune-ii.json');
export const BLACKLINE_RELAY: ClientMap = load(blacklineRelayJson, 'blackline-relay.json');
// The round-mode trio (2026-09-09, renamed 2026-09-10): brush generators in `tools/maps/`.
// Monteforte is the Italian hill village, Overgrowth the jungle temple, Reactor the power plant.
export const BLACKLINE_MONTEFORTE: ClientMap = load(blacklineMonteforteJson, 'blackline-monteforte.json');
export const BLACKLINE_OVERGROWTH: ClientMap = load(blacklineOvergrowthJson, 'blackline-overgrowth.json');
export const BLACKLINE_REACTOR: ClientMap = load(blacklineReactorJson, 'blackline-reactor.json');

export const CLIENT_MAPS: Record<string, ClientMap> = {
  [GREYBOX_01.definition.name]: GREYBOX_01,
  [BLACKLINE_KASBAH.definition.name]: BLACKLINE_KASBAH,
  [BLACKLINE_FOUNDRY.definition.name]: BLACKLINE_FOUNDRY,
  [BLACKLINE_HARBOUR.definition.name]: BLACKLINE_HARBOUR,
  [BLACKLINE_DUNE_II.definition.name]: BLACKLINE_DUNE_II,
  [BLACKLINE_RELAY.definition.name]: BLACKLINE_RELAY,
  [BLACKLINE_MONTEFORTE.definition.name]: BLACKLINE_MONTEFORTE,
  [BLACKLINE_OVERGROWTH.definition.name]: BLACKLINE_OVERGROWTH,
  [BLACKLINE_REACTOR.definition.name]: BLACKLINE_REACTOR,
};

/**
 * What the client predicts against before the server has said anything.
 *
 * Resolved from the shared `DEFAULT_MAP_NAME` rather than named again here, so the client and
 * the server cannot drift apart. It cannot simply wait for `WELCOME` and then load: prediction
 * needs a collision world before the first input, and a client that waited would spend its
 * first frames unpredicted. If the server turns out to be running something else,
 * `describeMapMismatch` says so loudly rather than letting the client correct on every tick.
 *
 * Throws at module load if the default is not bundled — which is the right moment to find out,
 * since the alternative is a client with no floor to predict against.
 */
export const DEFAULT_CLIENT_MAP: ClientMap = (() => {
  const map = CLIENT_MAPS[DEFAULT_MAP_NAME];
  if (!map) throw new Error(`default map "${DEFAULT_MAP_NAME}" is not bundled into the client`);
  return map;
})();

export function clientMap(name: string): ClientMap | undefined {
  return CLIENT_MAPS[name];
}

/**
 * Compares a server's advertised map against the one this client predicts with.
 * Returns null when they agree, or a sentence fit to put in front of a human.
 *
 * Name first, then hash: a different name is a configuration mistake and reads as
 * one, while a matching name with a different hash is the genuinely dangerous case —
 * a stale asset that looks correct. Pure and exported so the case that must never
 * regress (agreeing maps reported as mismatched, or vice versa) has a test rather
 * than a code path nobody has run.
 *
 * Each branch carries its own remedy, because they do not share one. The overlay used to
 * append a single generic "open /arena.html?map=<the server's map>" to whichever mismatch
 * fired, and that instruction cannot fix either case a player can actually reach: the client
 * now loads whatever WELCOME names, so a name mismatch means the server's map is *not in
 * this build* — `?map=` cannot conjure it — and a hash mismatch means the two sides built
 * the same name from different content, which no client-side URL changes. Printing a
 * remedy that does nothing costs more than printing none: it sends the player round a loop
 * before they report it.
 */
export function describeMapMismatch(
  expected: ClientMap,
  serverMapName: string,
  serverMapHash: number,
): string | null {
  if (serverMapName !== expected.definition.name) {
    return (
      `server is running map "${serverMapName}" but this client loaded "${expected.definition.name}"` +
      `\n\nthis build does not bundle the server's map — redeploy the client, or point the server at a map it has`
    );
  }
  if (serverMapHash !== expected.hash) {
    return (
      `map "${serverMapName}" differs: server hash ${formatMapHash(serverMapHash)}, client hash ${formatMapHash(expected.hash)} — the geometry is not the same, so prediction will correct on every tick` +
      `\n\nboth sides were built from different map content — redeploy so client and server ship the same shared/maps/${serverMapName}.json`
    );
  }
  return null;
}
